import React, { useEffect, useRef, useState, useCallback } from "react";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import aboutData from "../../data/about.json";
import {
  Shield,
  Target,
  Globe,
  Users,
  TrendingUp,
  Heart,
  Sparkles,
  ArrowRight,
} from "lucide-react";

/* ─────────────────────────────────────────────
 ICON MAP
───────────────────────────────────────────── */
const ICON_MAP = {
  Shield,
  Target,
  Globe,
  Users,
  TrendingUp,
  Heart,
};

/* ─────────────────────────────────────────────
 MAGNETIC HOOK
───────────────────────────────────────────── */
const useMagnetic = (strength = 0.35) => {
  const ref = useRef(null);

  const onMove = useCallback(
    (e) => {
      if (!ref.current) return;
      const { left, top, width, height } = ref.current.getBoundingClientRect();
      const x = (e.clientX - left - width / 2) * strength;
      const y = (e.clientY - top - height / 2) * strength;
      gsap.to(ref.current, { x, y, duration: 0.4, ease: "power3.out" });
    },
    [strength],
  );

  const onLeave = useCallback(() => {
    if (!ref.current) return;
    gsap.to(ref.current, {
      x: 0,
      y: 0,
      duration: 0.6,
      ease: "elastic.out(1, 0.4)",
    });
  }, []);

  return { ref, onMove, onLeave };
};

/* ─────────────────────────────────────────────
 SVG CIRCLE PROGRESS (draws on hover)
───────────────────────────────────────────── */
const CircleProgress = ({ color, isHovered }) => {
  const circleRef = useRef(null);

  useEffect(() => {
    if (!circleRef.current) return;
    const circumference = 2 * Math.PI * 42;
    gsap.to(circleRef.current, {
      strokeDashoffset: isHovered ? 0 : circumference,
      duration: 0.8,
      ease: "power3.out",
    });
  }, [isHovered]);

  const circumference = 2 * Math.PI * 42;

  return (
    <svg
      className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none"
      viewBox="0 0 100 100"
    >
      {/* Track */}
      <circle
        cx="50"
        cy="50"
        r="42"
        fill="none"
        stroke={`${color}15`}
        strokeWidth="1"
      />
      {/* Progress */}
      <circle
        ref={circleRef}
        cx="50"
        cy="50"
        r="42"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference}
        style={{ opacity: 0.6 }}
      />
    </svg>
  );
};

/* ─────────────────────────────────────────────
 PARTICLE BURST (on hover)
───────────────────────────────────────────── */
const ParticleBurst = ({ color, isActive }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const particles = [];
    for (let i = 0; i < 6; i++) {
      const dot = document.createElement("div");
      dot.style.cssText = `
 position: absolute;
 width: 3px; height: 3px;
 border-radius: 50%;
 background: ${color};
 top: 50%; left: 50%;
 pointer-events: none;
`;
      containerRef.current.appendChild(dot);
      particles.push(dot);
    }

    gsap.fromTo(
      particles,
      { x: 0, y: 0, scale: 1, opacity: 0.8 },
      {
        x: () => (Math.random() - 0.5) * 60,
        y: () => (Math.random() - 0.5) * 60,
        scale: 0,
        opacity: 0,
        duration: 0.7,
        stagger: 0.05,
        ease: "power2.out",
        onComplete: () => {
          particles.forEach((p) => p.remove());
        },
      },
    );
  }, [isActive, color]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none z-10"
    />
  );
};

/* ─────────────────────────────────────────────
 VALUE CARD (3D tilt + orbital icon + circle draw)
───────────────────────────────────────────── */
const ValueCard = ({ value, index }) => {
  const cardRef = useRef(null);
  const innerRef = useRef(null);
  const iconWrap = useRef(null);
  const glowRef = useRef(null);
  const statRef = useRef(null);
  const orbitRef = useRef(null);

  const [isHovered, setIsHovered] = useState(false);
  const [burst, setBurst] = useState(false);

  const IconC = ICON_MAP[value.icon] || Shield;
  const magnetic = useMagnetic(0.2);

  /* ── Scroll reveal ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardRef.current,
        { y: 60, opacity: 0, scale: 0.92 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.9,
          delay: index * 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cardRef.current,
            start: "top 88%",
          },
        },
      );
    }, cardRef);

    return () => ctx.revert();
  }, [index]);

  /* ── Orbit animation ── */
  useEffect(() => {
    if (!orbitRef.current) return;
    gsap.to(orbitRef.current, {
      rotation: 360,
      duration: 12 + index * 2,
      ease: "none",
      repeat: -1,
    });
  }, [index]);

  /* ── 3D Tilt ── */
  const handleMove = (e) => {
    if (!innerRef.current) return;
    const { left, top, width, height } =
      innerRef.current.getBoundingClientRect();
    const x = (e.clientX - left) / width - 0.5;
    const y = (e.clientY - top) / height - 0.5;

    gsap.to(innerRef.current, {
      rotateY: x * 12,
      rotateX: -y * 12,
      duration: 0.5,
      ease: "power2.out",
    });

    gsap.to(glowRef.current, {
      x: x * 40,
      y: y * 40,
      opacity: 0.8,
      duration: 0.5,
      ease: "power2.out",
    });
  };

  const handleEnter = () => {
    setIsHovered(true);
    setBurst(true);
    setTimeout(() => setBurst(false), 700);

    gsap.to(iconWrap.current, {
      scale: 1.15,
      duration: 0.4,
      ease: "back.out(2)",
    });

    gsap.to(statRef.current, {
      y: 0,
      opacity: 1,
      duration: 0.4,
      ease: "power3.out",
    });
  };

  const handleLeave = () => {
    setIsHovered(false);

    gsap.to(innerRef.current, {
      rotateY: 0,
      rotateX: 0,
      duration: 0.6,
      ease: "power3.out",
    });

    gsap.to(glowRef.current, {
      x: 0,
      y: 0,
      opacity: 0.3,
      duration: 0.6,
      ease: "power3.out",
    });

    gsap.to(iconWrap.current, {
      scale: 1,
      duration: 0.4,
      ease: "power3.out",
    });

    gsap.to(statRef.current, {
      y: 10,
      opacity: 0,
      duration: 0.3,
      ease: "power3.in",
    });
  };

  return (
    <div
      ref={cardRef}
      className="perspective-1000"
      style={{ opacity: 0 }}
      onMouseMove={(e) => {
        handleMove(e);
        magnetic.onMove(e);
      }}
      onMouseEnter={handleEnter}
      onMouseLeave={(e) => {
        handleLeave();
        magnetic.onLeave();
      }}
    >
      <div
        ref={innerRef}
        className="relative rounded-3xl p-7 cursor-default will-change-transform
 transform-gpu transition-shadow duration-300 h-full
 hover:shadow-card-hover"
        style={{
          background: `${value.color}06`,
          border: `1px solid ${value.color}20`,
          backdropFilter: "blur(16px)",
        }}
      >
        {/* Glow orb */}
        <div
          ref={glowRef}
          className="absolute inset-0 pointer-events-none rounded-3xl overflow-hidden"
          style={{ opacity: 0.3 }}
        >
          <div
            className="absolute w-48 h-48 rounded-full"
            style={{
              background: `radial-gradient(circle, ${value.color}20 0%, transparent 70%)`,
              top: "10%",
              left: "10%",
              filter: "blur(30px)",
            }}
          />
        </div>

        <ParticleBurst color={value.color} isActive={burst} />

        {/* ── ICON with orbiting dot + circle progress ── */}
        <div className="relative w-20 h-20 mb-6">
          <CircleProgress color={value.color} isHovered={isHovered} />

          {/* Orbiting dot */}
          <div ref={orbitRef} className="absolute inset-0 pointer-events-none">
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2
 w-2 h-2 rounded-full"
              style={{
                background: value.color,
                boxShadow: `0 0 8px ${value.color}80`,
                opacity: isHovered ? 1 : 0.4,
                transition: "opacity 0.3s",
              }}
            />
          </div>

          {/* Icon container */}
          <div
            ref={iconWrap}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{
                background: `${value.color}15`,
                border: `1px solid ${value.color}30`,
              }}
            >
              <IconC size={24} style={{ color: value.color }} />
            </div>
          </div>
        </div>

        {/* ── Number badge (top-right) ── */}
        <div
          className="absolute top-4 right-5 font-mono text-[10px] font-bold tracking-[0.2em]"
          style={{ color: `${value.color}40` }}
        >
          {String(index + 1).padStart(2, "0")}
        </div>

        {/* ── Content ── */}
        <div className="relative z-10 space-y-3">
          <h3 className="font-display font-bold text-gray-900 text-lg leading-snug">
            {value.title}
          </h3>

          <div
            className="h-px w-10 rounded-full transition-all duration-500"
            style={{
              background: isHovered
                ? `linear-gradient(90deg, ${value.color}, transparent)`
                : `${value.color}30`,
              width: isHovered ? "3rem" : "2rem",
            }}
          />

          <p className="text-gray-600 text-sm leading-relaxed">
            {value.description}
          </p>
        </div>

        {/* ── Hover stat reveal ── */}
        <div
          ref={statRef}
          className="absolute bottom-5 right-5 text-right"
          style={{ opacity: 0, transform: "translateY(10px)" }}
        >
          <p
            className="font-black text-2xl font-mono leading-none"
            style={{ color: value.color }}
          >
            {value.stats.value}
          </p>
          <p className="text-[9px] text-gray-500 mt-1 tracking-wider">
            {value.stats.label}
          </p>
        </div>

        {/* ── Bottom accent line ── */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[2px] rounded-b-3xl
 transition-transform duration-700 origin-left"
          style={{
            background: `linear-gradient(90deg, ${value.color}60, ${value.color}10)`,
            transform: isHovered ? "scaleX(1)" : "scaleX(0)",
          }}
        />

        {/* Watermark */}
        <div
          className="absolute -bottom-6 -right-2 font-black font-display
 leading-none pointer-events-none select-none"
          style={{
            fontSize: "7rem",
            color: value.color,
            opacity: 0.03,
          }}
        >
          {value.icon.charAt(0)}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 CENTER ORBIT GRAPHIC (decorative)
───────────────────────────────────────────── */
const CenterOrbit = () => {
  const ringRef1 = useRef(null);
  const ringRef2 = useRef(null);
  const ringRef3 = useRef(null);
  const coreRef = useRef(null);

  useEffect(() => {
    gsap.to(ringRef1.current, {
      rotation: 360,
      duration: 30,
      ease: "none",
      repeat: -1,
    });
    gsap.to(ringRef2.current, {
      rotation: -360,
      duration: 40,
      ease: "none",
      repeat: -1,
    });
    gsap.to(ringRef3.current, {
      rotation: 360,
      duration: 50,
      ease: "none",
      repeat: -1,
    });
    gsap.to(coreRef.current, {
      scale: 1.1,
      duration: 2,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
    });
  }, []);

  return (
    <div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
 pointer-events-none hidden xl:block"
    >
      {/* Ring 1 */}
      <div
        ref={ringRef1}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
 w-[500px] h-[500px] rounded-full border border-gray-200/30"
      >
        <div
          className="absolute top-0 left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2
 rounded-full bg-primary-500/30"
        />
      </div>

      {/* Ring 2 */}
      <div
        ref={ringRef2}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
 w-[350px] h-[350px] rounded-full border border-gray-200/40"
        style={{ borderStyle: "dashed" }}
      >
        <div
          className="absolute bottom-0 left-1/2 w-1.5 h-1.5 -translate-x-1/2 translate-y-1/2
 rounded-full bg-secondary-400/40"
        />
      </div>

      {/* Ring 3 */}
      <div
        ref={ringRef3}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
 w-[200px] h-[200px] rounded-full border border-gray-200/50"
      >
        <div
          className="absolute right-0 top-1/2 w-1 h-1 translate-x-1/2 -translate-y-1/2
 rounded-full bg-primary-400/50"
        />
      </div>

      {/* Core glow */}
      <div
        ref={coreRef}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
 w-4 h-4 rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(59,130,246,0.4) 0%, transparent 70%)",
          boxShadow: "0 0 40px rgba(59,130,246,0.2)",
        }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────
 MAIN CORE VALUES SECTION
───────────────────────────────────────────── */
const CoreValues = () => {
  const { values } = aboutData;

  const sectionRef = useRef(null);
  const headRef = useRef(null);
  const labelRef = useRef(null);
  const lineRef = useRef(null);
  const descRef = useRef(null);
  const bgRef = useRef(null);

  /* Header animation */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: labelRef.current,
          start: "top 78%",
        },
      });

      tl.fromTo(
        labelRef.current,
        { x: -20, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
      )
        .fromTo(
          headRef.current?.children ?? [],
          { y: 50, opacity: 0, skewY: 2 },
          {
            y: 0,
            opacity: 1,
            skewY: 0,
            stagger: 0.1,
            duration: 0.8,
            ease: "power4.out",
          },
          "-=0.2",
        )
        .fromTo(
          lineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.5,
            ease: "power3.out",
            transformOrigin: "center",
          },
          "-=0.4",
        )
        .fromTo(
          descRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
          "-=0.3",
        );
    });

    return () => ctx.revert();
  }, []);

  /* BG parallax */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(bgRef.current, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 2,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="core-values"
      className="relative overflow-hidden "
      style={{
        background:
          "linear-gradient(180deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)",
      }}
    >
      {/* ── BG ── */}
      {/* <div ref={bgRef} className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-pattern opacity-[0.04]" />

        <div
          className="absolute top-1/3 left-1/4 w-[600px] h-[600px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(29,78,216,0.08) 0%, transparent 60%)",
            filter: "blur(80px)",
          }}
        />
        <div
          className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(245,158,11,0.06) 0%, transparent 60%)",
            filter: "blur(60px)",
          }}
        />
      </div> */}
      {/* Edge fades */}
      <div
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none z-10"
        style={{
          background: "linear-gradient(to bottom, #ffffff, transparent)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none z-10"
        style={{ background: "linear-gradient(to top, #ffffff, transparent)" }}
      />
      {/* Decorative orbit */}
      <CenterOrbit />
      <div className="container-fluid relative z-20 ">
        {/* ── HEADER ── */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div ref={headRef} className="space-y-1  overflow-hidden">
            <h2
              className="font-display font-black text-gray-900 leading-tight"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
            >
              {values.headline}
            </h2>
            <h2
              className="font-display font-black text-gradient-primary leading-tight"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
            >
              {values.headlineAccent}
            </h2>
          </div>

          <div
            ref={lineRef}
            className="h-[2px] w-20 mx-auto mt-5 rounded-full"
            style={{
              background:
                "linear-gradient(90deg, transparent, #1d4ed8, #f59e0b, transparent)",
              transform: "scaleX(0)",
            }}
          />

          <p
            ref={descRef}
            className="text-gray-600 text-[15px] leading-relaxed mt-5 max-w-xl mx-auto"
            style={{ opacity: 0 }}
          >
            {values.description}
          </p>
        </div>

        {/* ── VALUE CARDS GRID ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
          {values.items.map((value, i) => (
            <ValueCard key={value.id} value={value} index={i} />
          ))}
        </div>

        {/* ── BOTTOM CTA LINE ── */}
        <div className="flex items-center justify-center gap-4 mt-16">
          <div
            className="h-px flex-1 max-w-24"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(29,78,216,0.3))",
            }}
          />
          <p className="text-xs font-semibold tracking-[0.3em] text-gray-500 uppercase flex items-center gap-2">
            Built on Purpose
            <ArrowRight size={12} className="text-primary-500" />
            Driven by Values
          </p>
          <div
            className="h-px flex-1 max-w-24"
            style={{
              background:
                "linear-gradient(90deg, rgba(245,158,11,0.3), transparent)",
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default CoreValues;
